import { useCallback, useEffect, useRef } from 'react';
import Shepherd from 'shepherd.js';
import { disposeTour, isTourInProgress, scheduleTour } from '@/lib/tourLifecycle';
import 'shepherd.js/dist/css/shepherd.css';
import '@/styles/tours.css';

/**
 * Page-specific tour configurations
 * Each page has a set of steps that highlight key features on that page.
 */


/**
 * Page Tour Configurations
 * Each page has an array of tour steps that describe its key features.
 */
export const pageTourConfigs = {
  // ============================================
  // PEOPLE SECTION
  // ============================================
  'people-employees': {
    pageKey: 'people-employees',
    steps: [
      {
        id: 'employees-overview',
        title: "Employee directory",
        text: `
          <p>Review employee records, account status and product access. The summary cards show active employees, counted seats, pending accounts and content-team membership.</p>
        `,
      },
      {
        id: 'employees-search',
        title: "Find an employee",
        text: `
          <p>Search by name or email and use the filters to narrow the list. Open a record to review its details, access and invitation status.</p>
        `,
        attachTo: { element: 'input[placeholder*="Search"]', on: 'bottom' },
      },
      {
        id: 'employees-bulk-import',
        title: "Import employee records",
        text: `
          <p>Use Bulk Import to upload a CSV, map its columns and review the preview before importing. Review account access and activation separately after import.</p>
        `,
        attachTo: { element: '[data-testid="bulk-import-btn"]', on: 'bottom' },
      },
    ],
  },
  
  'people-groups': {
    pageKey: 'people-groups',
    steps: [
      {
        id: 'groups-overview',
        title: "User groups",
        text: `
          <p>Organize employees into groups such as departments, locations or project teams.</p>
        `,
      },
      {
        id: 'groups-benefits',
        title: "Review membership",
        text: `
          <p>Select a group to see its members. Group membership is used when content or notifications target that group.</p>
        `,
      },
      {
        id: 'groups-create',
        title: "Create and edit groups",
        text: `
          <p>Give each group a clear name and description, then review its members. Check the intended audience before using a group for document access.</p>
        `,
      },
    ],
  },
  
  'people-content-team': {
    pageKey: 'people-content-team',
    steps: [
      {
        id: 'content-team-overview',
        title: "Content team",
        text: `
          <p>Manage who can use Eva Insights. Review each member’s role and invitation status here.</p>
        `,
      },
      {
        id: 'content-team-roles',
        title: "Choose the right role",
        text: `
          <p>Read the Role Permissions table before granting access. Admin, Approver, Analyst and Viewer have different permissions for content, reports and settings.</p>
        `,
      },
      {
        id: 'content-team-assign',
        title: "Invite a team member",
        text: `
          <p>Use Invite Team Member to choose an existing employee or enter a new colleague’s details. Confirm the role before sending the invitation.</p>
        `,
      },
    ],
  },
  
  'people-adoption': {
    pageKey: 'people-adoption',
    steps: [
      {
        id: 'adoption-overview',
        title: "Adoption",
        text: `
          <p>Review how often employees use Eva and when they were last active. Use this as a starting point for checking in with your team.</p>
        `,
      },
      {
        id: 'adoption-metrics',
        title: "Read the activity measures",
        text: `
          <p>MAU, WAU and DAU count monthly, weekly and daily active users. Stickiness compares daily activity with monthly activity.</p>
        `,
      },
      {
        id: 'adoption-insights',
        title: "Explore participation",
        text: `
          <p>Use the user and department views to see where activity is concentrated. Check with the people involved before deciding why usage is low.</p>
        `,
      },
    ],
  },
  
  // ============================================
  // CONVERSATIONS SECTION
  // ============================================
  'conversations-activity': {
    pageKey: 'conversations-activity',
    steps: [
      {
        id: 'activity-overview',
        title: "Conversation activity",
        text: `
          <p>Review recorded conversations and the questions employees asked. Use the available filters to focus your review.</p>
        `,
      },
      {
        id: 'activity-patterns',
        title: "Read the conversation",
        text: `
          <p>Open a conversation to review the question, answer and available supporting details. Read follow-ups for context before judging the answer.</p>
        `,
      },
      {
        id: 'activity-action',
        title: "Decide what needs attention",
        text: `
          <p>Compare the answer with its sources and any feedback. Check existing knowledge before deciding that a new document is needed.</p>
        `,
      },
    ],
  },
  
  'conversations-trending': {
    pageKey: 'conversations-trending',
    steps: [
      {
        id: 'trending-overview',
        title: "Trending topics",
        text: `
          <p>Review frequently discussed topics and changes in activity for the selected period.</p>
        `,
      },
      {
        id: 'trending-analysis',
        title: "Read trends in context",
        text: `
          <p>Compare the question counts and trend indicators. A change in volume is a reason to investigate the questions, rather than a measure of answer quality.</p>
        `,
      },
    ],
  },
  
  'conversations-feedback': {
    pageKey: 'conversations-feedback',
    steps: [
      {
        id: 'feedback-overview',
        title: "Answer feedback",
        text: `
          <p>Review ratings and comments submitted about Eva’s answers. Open the related conversation to understand the feedback.</p>
        `,
      },
      {
        id: 'feedback-sentiment',
        title: "Read the feedback summary",
        text: `
          <p>Compare helpful and unhelpful ratings for the selected period. Check the number of ratings as well as the percentage.</p>
        `,
      },
      {
        id: 'feedback-action',
        title: "Follow up on feedback",
        text: `
          <p>Read the comment, answer and sources together. Decide whether the issue needs clearer content, a different answer or a product fix.</p>
        `,
      },
    ],
  },
  
  // ============================================
  // KNOWLEDGE BASE SECTION
  // ============================================
  'kb-content': {
    pageKey: 'kb-content',
    steps: [
      {
        id: 'kb-overview',
        title: "Knowledge Base content",
        text: `
          <p>Manage the documents Eva can retrieve when answering questions. A document must be published, successfully processed and accessible to the user before it can support an answer.</p>
        `,
      },
      {
        id: 'kb-tabs',
        title: "Organize documents",
        text: `
          <p>Use folders and the document list to find content. Folders organize files; document visibility determines who can use them.</p>
        `,
      },
      {
        id: 'kb-upload',
        title: "Upload documents",
        text: `
          <p>Upload a file or a batch using the supported formats and limits shown in the upload dialog. New uploads start as drafts, and processing can begin in the background.</p>
        `,
      },
      {
        id: 'kb-workflow-draft',
        title: "Review the draft",
        text: `
          <p>Open the document to review its metadata. Ask Eva can suggest fields such as topic and summary using knowledge base credits. Check suggestions against the source before saving.</p>
        `,
      },
      {
        id: 'kb-workflow-review',
        title: "Submit and approve",
        text: `
          <p>Submit a draft for review when it is ready. An authorized reviewer can approve it or return it to Draft for changes.</p>
        `,
      },
      {
        id: 'kb-workflow-ingestion',
        title: "Publish and check processing",
        text: `
          <p>After approval, use Start Ingestion to publish the document and complete any required processing. Check its status for completion or an error.</p>
        `,
      },
      {
        id: 'kb-workflow-live',
        title: "Check access and the answer",
        text: `
          <p>A Live document is ready for retrieval within its configured visibility. Ask a question about it in Eva People and check the cited source.</p>
        `,
      },
      {
        id: 'kb-status-filters',
        title: "Filter by status",
        text: `
          <p>Filter the list to find drafts, documents awaiting review, approved documents or published content. Check processing status as well as publication status.</p>
        `,
      },
      {
        id: 'kb-document-actions',
        title: "Document actions",
        text: `
          <p>Open a document for its details, or use its row menu for available actions. Options such as Download, Move to Folder and Archive depend on your role and the document’s status.</p>
        `,
      },
    ],
  },
  
  'kb-coverage': {
    pageKey: 'kb-coverage',
    steps: [
      {
        id: 'coverage-overview',
        title: "Knowledge Base coverage",
        text: `
          <p>Review the documents included in coverage, their geographic scope and page counts. Use the available views to explore the content behind these totals.</p>
        `,
      },
      {
        id: 'coverage-metrics',
        title: "Read coverage measures",
        text: `
          <p>Compare global and country-specific documents and the countries represented. These measures describe the content inventory; review actual answers to assess its usefulness.</p>
        `,
      },
      {
        id: 'coverage-action',
        title: "Investigate missing information",
        text: `
          <p>Filter the document list by scope or search for the subject you need. Compare existing sources with reported questions and gaps before adding content.</p>
        `,
      },
    ],
  },
  
  'kb-topics': {
    pageKey: 'kb-topics',
    steps: [
      {
        id: 'topics-overview',
        title: "Topics",
        text: `
          <p>Use topics to categorize documents and conversations. Choose names that your team can recognize.</p>
        `,
      },
      {
        id: 'topics-hierarchy',
        title: "Topics and subtopics",
        text: `
          <p>Use subtopics for more specific subjects under a topic. For example, Events could contain Registration and Sponsorship.</p>
        `,
      },
      {
        id: 'topics-manage',
        title: "Maintain topics",
        text: `
          <p>Use the available controls to add, edit or deprecate topics and subtopics. Review the impact on assigned content before making a change.</p>
        `,
      },
    ],
  },
  
  'kb-gaps': {
    pageKey: 'kb-gaps',
    steps: [
      {
        id: 'gaps-overview',
        title: "Knowledge gaps",
        text: `
          <p>Review potential gaps identified from questions and feedback. Check the original question and current sources to decide whether information is missing or an answer needs correction.</p>
        `,
      },
      {
        id: 'gaps-prioritize',
        title: "Review and resolve gaps",
        text: `
          <p>Use the recorded frequency and details to prioritize review. Resolve a gap after checking that the needed information is available and the answer works.</p>
        `,
      },
    ],
  },
  
  // ============================================
  // ENGAGEMENT SECTION
  // ============================================
  'engagement-leaderboard': {
    pageKey: 'engagement-leaderboard',
    steps: [
      {
        id: 'leaderboard-overview',
        title: "Engagement leaderboard",
        text: `
          <p>Review participation recorded in Eva, including points and activity streaks.</p>
        `,
      },
      {
        id: 'leaderboard-top-performers',
        title: "Leading participants",
        text: `
          <p>The podium highlights the highest point totals in the selected view. Points reflect recorded participation, so review them in context.</p>
        `,
      },
      {
        id: 'leaderboard-metrics',
        title: "Understand points",
        text: `
          <p>Questions, feedback and other supported activities can earn points. Use the displayed activity measures to understand each participant’s total.</p>
        `,
      },
      {
        id: 'leaderboard-table',
        title: "Explore the leaderboard",
        text: `
          <p>Review each participant’s points, level and activity in the table. Open available details when you want more context.</p>
        `,
      },
      {
        id: 'leaderboard-filters',
        title: "Choose a view",
        text: `
          <p>Use the time range and search controls to focus the leaderboard. Compare participants within the same selected period.</p>
        `,
      },
    ],
  },
  
  'engagement-notifications': {
    pageKey: 'engagement-notifications',
    steps: [
      {
        id: 'notifications-overview',
        title: "Notifications",
        text: `
          <p>Create and manage messages for employees. Review the message, audience and delivery channel before sending.</p>
        `,
      },
      {
        id: 'notifications-stats',
        title: "Read notification activity",
        text: `
          <p>Review the sent, delivery, read and click measures shown here. Read and click activity helps you assess engagement after a message is sent.</p>
        `,
      },
      {
        id: 'notifications-create',
        title: "Create a notification",
        text: `
          <p>Choose Create Notification, enter the message and select all users, groups or individual recipients. Check the selected audience before proceeding.</p>
        `,
      },
      {
        id: 'notifications-workflow',
        title: "Save, schedule or send",
        text: `
          <p>Save a draft for later, choose a future schedule or send now. Check the resulting status and any reported failure.</p>
        `,
      },
      {
        id: 'notifications-delivery',
        title: "Choose delivery channels",
        text: `
          <p>Choose Eva People, email or both. Use the available read and click activity to follow up; a sent status alone does not establish that someone read the message.</p>
        `,
      },
    ],
  },
  
  'engagement-referral': {
    pageKey: 'engagement-referral',
    steps: [
      {
        id: 'referral-overview',
        title: "Referrals",
        text: `
          <p>Review the referral program offered to your organization, including its current eligibility and reward details.</p>
        `,
      },
      {
        id: 'referral-how',
        title: "Share your referral link",
        text: `
          <p>Copy your referral link when you want to invite another organization to try Eva. Review the displayed program terms before sharing it.</p>
        `,
      },
      {
        id: 'referral-rewards',
        title: "Track referral status",
        text: `
          <p>Review each referral’s status and recorded reward. The balance and reward history show what has been credited under the program.</p>
        `,
      },
    ],
  },
  
  'engagement-reports': {
    pageKey: 'engagement-reports',
    steps: [
      {
        id: 'reports-overview',
        title: "Reports",
        text: `
          <p>Create reports from the activity recorded in your workspace. Select the report type and date range you need.</p>
        `,
      },
      {
        id: 'reports-templates',
        title: "Choose content and format",
        text: `
          <p>Select from the available report types, such as Usage, Feedback or Knowledge Base. Choose HTML, PDF or CSV and review the date range before exporting.</p>
        `,
      },
      {
        id: 'reports-schedule',
        title: "Scheduled reports",
        text: `
          <p>If your role allows scheduling, use Scheduled Reports to choose a frequency and recipients. Review execution history to check the result of scheduled runs.</p>
        `,
      },
    ],
  },
  
  // ============================================
  // CONNECTORS
  // ============================================
  connectors: {
    pageKey: 'connectors',
    steps: [
      {
        id: 'connectors-overview',
        title: "Connectors",
        text: `
          <p>This is where you configure supported connections and explore what is coming next. More connections to your existing tools are in development.</p>
        `,
      },
      {
        id: 'connectors-available',
        title: "Review available connections",
        text: `
          <p>Use the SharePoint import controls to connect and select files for review. Cards marked Coming Soon show planned connections, and you can vote for the ones your team needs.</p>
        `,
      },
      {
        id: 'connectors-setup',
        title: "Import with the right access",
        text: `
          <p>SharePoint imports create draft copies in Eva. Review their content and Eva visibility before publishing. Changes and permissions in SharePoint are not automatically mirrored to those copies.</p>
        `,
      },
    ],
  },
  
  // ============================================
  // SETTINGS
  // ============================================
  settings: {
    pageKey: 'settings',
    steps: [
      {
        id: 'settings-overview',
        title: "Organization settings",
        text: `
          <p>Review your organization’s profile, branding and enabled experiences. Available settings depend on your role.</p>
        `,
      },
      {
        id: 'settings-branding',
        title: "Branding and theme",
        text: `
          <p>Use the branding controls to configure logos, colors and the default theme. Check both light and dark appearances after saving.</p>
        `,
      },
      {
        id: 'settings-org',
        title: "Organization details",
        text: `
          <p>Review the organization details and preferences shown here. Save your changes, then check that the updated values appear as intended.</p>
        `,
      },
    ],
  },

  'settings-billing': {
    pageKey: 'settings-billing',
    steps: [
      {
        id: 'billing-overview',
        title: "Billing and plan",
        text: `
          <p>Review your current subscription, seats and billing details. Check the displayed status and renewal information before making changes.</p>
        `,
      },
      {
        id: 'billing-plans-seats',
        title: "Plan and seat changes",
        text: `
          <p>Choose the plan and total seat count you need. Review the quote, effective date and billing impact before confirming a change.</p>
        `,
      },
      {
        id: 'billing-spend-invoices',
        title: "Payment history",
        text: `
          <p>Review recorded payments and open an invoice when a link is available. Use the billing controls to review your payment details.</p>
        `,
      },
    ],
  },

  'settings-usage': {
    pageKey: 'settings-usage',
    steps: [
      {
        id: 'usage-overview',
        title: "AI credits",
        text: `
          <p>Review AI credit usage and remaining capacity for the period shown. Check conversation and knowledge base usage where they are listed separately.</p>
        `,
      },
      {
        id: 'usage-key-metrics',
        title: "Explore usage",
        text: `
          <p>Use the time range and available breakdowns to see where credits are being used. Compare the usage totals with the allowance shown for your workspace.</p>
        `,
      },
      {
        id: 'usage-staying-within-capacity',
        title: "Plan for upcoming work",
        text: `
          <p>Check remaining credits before a large upload or testing session. Review Billing and Plan if your workspace needs a different allowance.</p>
        `,
      },
    ],
  },

  // ============================================
  // OVERVIEW PAGE
  // ============================================
  overview: {
    pageKey: 'overview',
    steps: [
      {
        id: 'overview-broadcast',
        title: "People activity",
        text: `
          <p>This feed shows recorded Eva People activity as updates arrive. Use the conversation and feedback pages to investigate the underlying details.</p>
        `,
        attachTo: { element: '[data-testid="eva-people-ticker"]', on: 'bottom' },
      },
      {
        id: 'overview-kpis',
        title: "Workspace summary",
        text: `
          <p>Review employees using Eva, questions answered, helpful ratings and Knowledge Base coverage. Open a summary card to explore its data and reporting period.</p>
        `,
        attachTo: { element: '[data-testid="overview-kpi-cards"]', on: 'bottom' },
      },
      {
        id: 'overview-alerts',
        title: "Needs attention",
        text: `
          <p>Review the actions shown for your role, such as knowledge gaps, feedback or capacity alerts. Follow an item to investigate it before making changes.</p>
        `,
        attachTo: { element: '[data-testid="overview-alerts-card"]', on: 'right' },
      },
      {
        id: 'overview-trending',
        title: "Trending topics",
        text: `
          <p>See which topics appear frequently in recorded conversations. Open the trends view to examine the questions and changes over time.</p>
        `,
        attachTo: { element: '[data-testid="overview-trending-card"]', on: 'left' },
      },
    ],
  },
};

/**
 * Custom hook for page-specific tours
 */
export function usePageTour() {
  const tourRef = useRef(null);
  
  const startTour = useCallback((pageKey) => {
    const config = pageTourConfigs[pageKey];
    if (!config || !config.steps || config.steps.length === 0) {
      console.warn(`No tour configuration found for page: ${pageKey}`);
      return;
    }
    
    if (isTourInProgress()) return;
    
    const previousFocus = document.activeElement;
    // Create new tour
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: { behavior: 'smooth', block: 'center' },
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 8,
        classes: 'shepherd-page-tour',
        popperOptions: {
          modifiers: [{ name: 'offset', options: { offset: [0, 12] } }],
        },
      },
    });
    const restoreFocus = () => queueMicrotask(() => {
      const target = previousFocus?.isConnected && previousFocus !== document.body
        ? previousFocus
        : document.querySelector('main h1') || document.querySelector('[data-testid="user-menu-trigger"]');
      if (!target) return;
      if (!target.hasAttribute('tabindex') && target.tagName === 'H1') {
        target.setAttribute('tabindex', '-1');
        target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
      }
      target.focus();
    });
    tour.on('complete', restoreFocus);
    tour.on('cancel', restoreFocus);
    
    // Add steps
    config.steps.forEach((step, index) => {
      const isFirst = index === 0;
      const isLast = index === config.steps.length - 1;
      const totalSteps = config.steps.length;
      
      // Add step counter to text
      const stepCounter = `<div class="shepherd-step-counter">Step ${index + 1} of ${totalSteps}</div>`;
      
      tour.addStep({
        id: step.id,
        title: step.title,
        text: stepCounter + step.text,
        attachTo: step.attachTo,
        buttons: [
          ...(!isFirst ? [{
            text: 'Back',
            action: tour.back,
            classes: 'shepherd-button-secondary',
          }] : []),
          {
            text: isLast ? 'Done' : 'Next',
            action: isLast ? tour.complete : tour.next,
            classes: 'shepherd-button-primary',
          },
        ],
        when: {
          show: () => {
            // Add highlight to target element
            if (step.attachTo?.element) {
              const target = document.querySelector(step.attachTo.element);
              if (target) {
                target.classList.add('shepherd-page-tour-highlight');
              }
            }
          },
          hide: () => {
            // Remove highlight from target element
            if (step.attachTo?.element) {
              const target = document.querySelector(step.attachTo.element);
              if (target) {
                target.classList.remove('shepherd-page-tour-highlight');
              }
            }
          },
        },
      });
    });
    
    // Start the tour with a small delay
    tourRef.current = tour;
    scheduleTour(tour, 300);
    
    return tour;
  }, []);
  
  const cancelTour = useCallback(() => {
    if (tourRef.current) {
      disposeTour(tourRef.current);
      tourRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    const tour = tourRef.current;
    if (!tour) return;
    tour.off('complete');
    tour.off('cancel');
    disposeTour(tour);
    tourRef.current = null;
  }, []);
  
  return { startTour, cancelTour };
}

export default usePageTour;
